/**
 * PRD-1623: the datasets a question used changed from the one before it. Returns
 * { added: [...], removed: [...] } (names, sorted), or null when nothing changed or there is
 * no earlier question. Built from each question's own dataset list, so it shows what the
 * answer used, stamped with the question's time (the Archived banner waits for PRD-1595).
 */
export function datasetChange(previous, current) {
  if (!previous || !current) return null;
  const before = new Set(previous);
  const after = new Set(current);
  const added = [...after].filter((n) => !before.has(n)).sort();
  const removed = [...before].filter((n) => !after.has(n)).sort();
  return added.length || removed.length ? { added, removed } : null;
}

/**
 * Shared Queries handoff 2 (PRD-1644): in a shared query the change comes from the backend's
 * `dataset_change` on /turns, which names only datasets this viewer can read and counts the
 * rest. Returns the same shape as datasetChange, plus the counts, or null for no change.
 */
export function sharedDatasetChange(change) {
  if (!change) return null;
  const result = {
    added: change.added || [],
    removed: change.removed || [],
    addedOthers: change.added_others || 0,
    removedOthers: change.removed_others || 0,
  };
  const any = result.added.length || result.removed.length || result.addedOthers || result.removedOthers;
  return any ? result : null;
}

const list = (names) => (names.length === 1 ? names[0] : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`);

const datasets = (n) => `${n} dataset${n === 1 ? '' : 's'}`;

// "Contract Rates", "Contract Rates, Maintenance History and 1 other dataset", "2 datasets".
const phrase = (names, others) => {
  if (!others) return list(names);
  if (!names.length) return datasets(others);
  return `${names.join(', ')} and ${others} other ${others === 1 ? 'dataset' : 'datasets'}`;
};

/** "Contract Rates added.", "Asset Register removed.", or both (Mixed). Unnamed ones are counted. */
export function datasetChangeText({ added, removed, addedOthers = 0, removedOthers = 0 }) {
  const parts = [];
  if (added.length || addedOthers) parts.push(`${phrase(added, addedOthers)} added`);
  if (removed.length || removedOthers) parts.push(`${phrase(removed, removedOthers)} removed`);
  return `${parts.join('; ')}. Answers below draw on the new selection; earlier answers are unchanged.`;
}

export function datasetChangeIcon({ added, removed, addedOthers = 0, removedOthers = 0 }) {
  const anyAdded = added.length || addedOthers;
  const anyRemoved = removed.length || removedOthers;
  if (anyAdded && anyRemoved) return 'stack';
  return anyAdded ? 'stack-plus' : 'stack-minus';
}
